import { Loader2, X } from "lucide-react";
import React, { useEffect } from "react";
import { SlabButton } from "src/components/ui/SlabButton";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Body copy — string or richer nodes. */
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red, chunky confirm styling for irreversible actions. */
  destructive?: boolean;
  /** Disables buttons and shows a spinner on confirm while an async op runs. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Reusable confirmation modal. Backdrop click or Escape cancels; the confirm
 * button carries the (optionally destructive) primary action. Matches the
 * app's white dialog shell (see EditHappeningNowDialog).
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-[400] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
    >
      <button
        type="button"
        aria-label="Cancel"
        tabIndex={-1}
        onClick={() => !busy && onCancel()}
        className="absolute inset-0 cursor-default"
      />
      <div
        className="relative w-full rounded-t-[2rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:max-w-md sm:rounded-[2rem] sm:p-7"
        style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2
            id="confirm-dialog-title"
            className="text-xl font-extrabold text-[#3C3C3C]"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={() => !busy && onCancel()}
            aria-label="Close"
            className="rounded-xl p-2 text-[#777777] hover:bg-[#F7F7F7] hover:text-[#3C3C3C]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6 text-sm font-bold leading-relaxed text-[#777777]">
          {message}
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <button
            type="button"
            onClick={() => !busy && onCancel()}
            disabled={busy}
            className="min-h-11 rounded-2xl border-2 border-[#E5E5E5] bg-white px-5 text-sm font-extrabold text-[#3C3C3C] shadow-[0_4px_0_#D0D0D0] transition-all hover:bg-[#F7F7F7] active:translate-y-0.5 active:shadow-[0_2px_0_#D0D0D0] disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <SlabButton
            type="button"
            variant="primary"
            onClick={onConfirm}
            disabled={busy}
            backgroundColor={destructive ? "#EF4444" : "#14B8A6"}
            lipColor={destructive ? "#B91C1C" : "#0F766E"}
            shadowLiftPx={5}
            shadowLiftActivePx={2}
            className="px-5 disabled:opacity-60"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Working…
              </>
            ) : (
              confirmLabel
            )}
          </SlabButton>
        </div>
      </div>
    </div>
  );
};
